import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  KeyRound, Shield, Users, Activity, Plus, Trash2, Edit2,
  ToggleLeft, ToggleRight, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, Eye, EyeOff, AlertTriangle,
  Download, RefreshCw, FileCode,
} from "lucide-react";
import { apiClient } from "../services/api";

const TABS = [
  { id: "dashboard", icon: Activity },
  { id: "providers", icon: Shield },
  { id: "sessions", icon: Users },
];

const PROVIDER_TYPES = ["azure_ad", "okta", "google", "oidc", "saml"];

const TYPE_COLORS = {
  azure_ad: "bg-blue-100 text-blue-800",
  okta: "bg-sky-100 text-sky-800",
  google: "bg-red-100 text-red-800",
  oidc: "bg-purple-100 text-purple-800",
  saml: "bg-orange-100 text-orange-800",
};

const STATUS_COLORS = {
  completed: "text-green-600 bg-green-50",
  failed: "text-red-600 bg-red-50",
  pending: "text-yellow-600 bg-yellow-50",
};

export default function SSOHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";

  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <KeyRound className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{t("ssoHub.title")}</h1>
            <p className="text-muted-foreground text-sm">
              {t("ssoHub.subtitle")}
            </p>
          </div>
          <span className="ml-auto bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded-full">
            {t("ssoHub.badge")}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(`ssoHub.tabs.${tab.id}`)}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "providers" && <ProvidersTab />}
        {activeTab === "sessions" && <SessionsTab />}
      </div>
    </div>
  );
}

/* ── Dashboard ──────────────────────────────────────────────────────────────── */
function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("v1/sso/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <LoadingState />;

  const kpis = [
    { label: t("ssoHub.dashboard.kpiActive"), value: `${data.active_providers} / ${data.total_providers}`, icon: Shield, color: "text-blue-600" },
    { label: t("ssoHub.dashboard.kpiSessions"), value: data.total_sessions, icon: Activity, color: "text-purple-600" },
    { label: t("ssoHub.dashboard.kpiCompleted"), value: data.completed_sessions, icon: CheckCircle, color: "text-green-600" },
    { label: t("ssoHub.dashboard.kpiSuccessRate"), value: `${data.success_rate}%`, icon: Users, color: "text-primary" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-card border rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{k.label}</span>
                <Icon className={`h-5 w-5 ${k.color}`} />
              </div>
              <div className="text-2xl font-bold">{k.value}</div>
            </div>
          );
        })}
      </div>

      {/* Recent sessions */}
      <div className="bg-card border rounded-xl p-5">
        <h3 className="font-semibold mb-4">{t("ssoHub.dashboard.recentTitle")}</h3>
        {data.recent_sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ssoHub.dashboard.noSessions")}</p>
        ) : (
          <div className="space-y-2">
            {data.recent_sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || "text-gray-600 bg-gray-50"}`}>
                    {s.status}
                  </span>
                  <span className="text-sm text-muted-foreground">{s.ip_address || "—"}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Setup guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {t("ssoHub.dashboard.guideTitle")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-800 mb-1.5">{t("ssoHub.dashboard.oidcTitle")}</p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>{t("ssoHub.dashboard.oidcStep1")}</li>
              <li>{t("ssoHub.dashboard.oidcStep2Pre")}<code className="bg-blue-100 px-1 rounded">/v1/sso/callback</code>{t("ssoHub.dashboard.oidcStep2Post")}</li>
              <li>{t("ssoHub.dashboard.oidcStep3")}</li>
            </ol>
          </div>
          <div>
            <p className="text-sm font-semibold text-blue-800 mb-1.5">{t("ssoHub.dashboard.samlTitle")}</p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>{t("ssoHub.dashboard.samlStep1Pre")}<strong>{t("ssoHub.dashboard.samlStep1Strong")}</strong></li>
              <li>{t("ssoHub.dashboard.samlStep2Pre")}<strong>{t("ssoHub.dashboard.samlStep2Strong")}</strong></li>
              <li>{t("ssoHub.dashboard.samlStep3Pre")}<strong>{t("ssoHub.dashboard.samlStep3Strong")}</strong>{t("ssoHub.dashboard.samlStep3Post")}</li>
              <li>{t("ssoHub.dashboard.samlStep4Pre")}<code className="bg-blue-100 px-1 rounded">/v1/sso/saml/[id]/acs</code></li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Providers ──────────────────────────────────────────────────────────────── */
function ProvidersTab() {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [presets, setPresets] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    apiClient.get("v1/sso/providers").then((r) => setProviders(r.data)).catch(() => {});
    apiClient.get("v1/sso/presets").then((r) => setPresets(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t("ssoHub.providers.confirmDelete"))) return;
    await apiClient.delete(`v1/sso/providers/${id}`);
    load();
  };

  const handleToggle = async (id) => {
    await apiClient.post(`v1/sso/providers/${id}/toggle`);
    load();
  };

  const handleSave = async (data) => {
    if (editing) {
      await apiClient.put(`v1/sso/providers/${editing.id}`, data);
    } else {
      await apiClient.post("v1/sso/providers", data);
    }
    setShowModal(false);
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> {t("ssoHub.providers.add")}
        </button>
      </div>

      {providers.length === 0 ? (
        <EmptyState
          icon={Shield}
          title={t("ssoHub.providers.emptyTitle")}
          description={t("ssoHub.providers.emptyDesc")}
        />
      ) : (
        <div className="grid gap-4">
          {providers.map((p) => (
            <ProviderCard
              key={p.id}
              provider={p}
              onEdit={() => { setEditing(p); setShowModal(true); }}
              onDelete={() => handleDelete(p.id)}
              onToggle={() => handleToggle(p.id)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <ProviderModal
          provider={editing}
          presets={presets}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ProviderCard({ provider, onEdit, onDelete, onToggle }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isSaml = provider.provider_type === "saml";
  const typeLabel = t(`ssoHub.providerTypes.${provider.provider_type}`, { defaultValue: provider.provider_type });
  const typeColor = TYPE_COLORS[provider.provider_type] || "bg-gray-100 text-gray-800";

  const downloadMetadata = () => {
    window.open(`/api/v1/sso/saml/${provider.id}/metadata`, "_blank");
  };

  return (
    <div className="bg-card border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">{provider.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor}`}>
                {typeLabel}
              </span>
              {provider.is_active ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{t("ssoHub.providers.active")}</span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{t("ssoHub.providers.inactive")}</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isSaml
                ? <>{t("ssoHub.providers.idpLabel")}<code className="bg-muted px-1 rounded text-xs">{provider.idp_entity_id || t("ssoHub.providers.notConfigured")}</code></>
                : <>{t("ssoHub.providers.clientIdLabel")}<code className="bg-muted px-1 rounded text-xs">{provider.client_id}</code></>
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSaml && (
            <button
              onClick={downloadMetadata}
              title={t("ssoHub.providers.downloadMetaTitle")}
              className="p-1.5 hover:bg-accent rounded text-blue-600"
            >
              <FileCode className="h-4 w-4" />
            </button>
          )}
          <button onClick={onToggle} className="p-1.5 hover:bg-accent rounded">
            {provider.is_active ? (
              <ToggleRight className="h-5 w-5 text-green-600" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
            )}
          </button>
          <button onClick={onEdit} className="p-1.5 hover:bg-accent rounded">
            <Edit2 className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="p-1.5 hover:bg-accent rounded text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-accent rounded">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-3 text-sm">
          {isSaml ? (
            <>
              {[
                [t("ssoHub.providers.detailIdpEntityId"), provider.idp_entity_id],
                [t("ssoHub.providers.detailIdpSsoUrl"), provider.idp_sso_url],
                [t("ssoHub.providers.detailSpEntityId"), provider.sp_entity_id],
                [t("ssoHub.providers.detailIdpCert"), provider.idp_certificate ? t("ssoHub.providers.certConfigured") : t("ssoHub.providers.certNotConfigured")],
                [t("ssoHub.providers.detailSignedRequests"), provider.sign_requests ? t("ssoHub.providers.yes") : t("ssoHub.providers.no")],
                [t("ssoHub.providers.detailAssertionsSigned"), provider.want_assertions_signed ? t("ssoHub.providers.yes") : t("ssoHub.providers.no")],
                [t("ssoHub.providers.detailDefaultRole"), provider.default_role],
                [t("ssoHub.providers.detailAutoProvision"), provider.auto_create_users ? t("ssoHub.providers.yes") : t("ssoHub.providers.no")],
              ].map(([label, value]) => (
                <div key={label}>
                  <span className="text-muted-foreground">{label}</span>
                  <p className="font-medium truncate">{value || "—"}</p>
                </div>
              ))}
              <div className="col-span-2">
                <span className="text-muted-foreground">{t("ssoHub.providers.acsUrlLabel")}</span>
                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                  /v1/sso/saml/{provider.id}/acs
                </p>
              </div>
              <div className="col-span-2">
                <button
                  onClick={downloadMetadata}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Download className="h-4 w-4" /> {t("ssoHub.providers.downloadMetaLink")}
                </button>
              </div>
            </>
          ) : (
            <>
              {[
                [t("ssoHub.providers.detailDiscoveryUrl"), provider.discovery_url],
                [t("ssoHub.providers.detailAuthUrl"), provider.authorization_url],
                [t("ssoHub.providers.detailTokenUrl"), provider.token_url],
                [t("ssoHub.providers.detailUserinfoUrl"), provider.userinfo_url],
                [t("ssoHub.providers.detailTenantId"), provider.tenant_id],
                [t("ssoHub.providers.detailDefaultRole"), provider.default_role],
                [t("ssoHub.providers.detailAutoProvision"), provider.auto_create_users ? t("ssoHub.providers.yes") : t("ssoHub.providers.no")],
                [t("ssoHub.providers.detailEmailAttr"), provider.attr_email],
              ].map(([label, value]) => (
                <div key={label}>
                  <span className="text-muted-foreground">{label}</span>
                  <p className="font-medium truncate">{value || "—"}</p>
                </div>
              ))}
              <div className="col-span-2">
                <span className="text-muted-foreground">{t("ssoHub.providers.callbackUrlLabel")}</span>
                <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">/v1/sso/callback</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProviderModal({ provider, presets, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    provider || {
      name: "",
      provider_type: "azure_ad",
      client_id: "",
      client_secret: "",
      tenant_id: "",
      discovery_url: "",
      authorization_url: "",
      token_url: "",
      userinfo_url: "",
      idp_metadata_url: "",
      idp_entity_id: "",
      idp_sso_url: "",
      idp_slo_url: "",
      idp_certificate: "",
      sp_entity_id: "",
      sp_acs_url_override: "",
      sign_requests: false,
      want_assertions_signed: true,
      attr_email: "email",
      attr_first_name: "given_name",
      attr_last_name: "family_name",
      auto_create_users: true,
      default_role: "EMPLOYE",
      is_active: true,
    }
  );
  const [showSecret, setShowSecret] = useState(false);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const isSaml = form.provider_type === "saml";

  const applyPreset = (type) => {
    if (type === "saml") {
      setForm((f) => ({ ...f, provider_type: "saml", name: f.name || "SAML 2.0" }));
      return;
    }
    const p = presets[type];
    if (!p) { setForm((f) => ({ ...f, provider_type: type })); return; }
    setForm((f) => ({
      ...f,
      provider_type: type,
      name: p.name || f.name,
      discovery_url: p.discovery_url || "",
      authorization_url: p.authorization_url || "",
      token_url: p.token_url || "",
      userinfo_url: p.userinfo_url || "",
      attr_email: p.attr_email || "email",
      attr_first_name: p.attr_first_name || "given_name",
      attr_last_name: p.attr_last_name || "family_name",
    }));
  };

  const fetchIdpMetadata = async () => {
    if (!provider?.id) return;
    setFetchingMeta(true);
    try {
      const r = await apiClient.post(`v1/sso/saml/${provider.id}/fetch-idp-metadata`);
      setForm((f) => ({
        ...f,
        idp_entity_id: r.data.idp_entity_id || f.idp_entity_id,
        idp_sso_url: r.data.idp_sso_url || f.idp_sso_url,
      }));
    } catch {
      // silently ignore — user can fill manually
    } finally {
      setFetchingMeta(false);
    }
  };

  const Field = ({ label, name, type = "text", placeholder = "" }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type={type}
        value={form[name] || ""}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        placeholder={placeholder}
        className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
      />
    </div>
  );

  const TextArea = ({ label, name, placeholder = "", rows = 4 }) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <textarea
        rows={rows}
        value={form[name] || ""}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        placeholder={placeholder}
        className="w-full border rounded-lg px-3 py-2 text-sm bg-background font-mono text-xs"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold text-lg">
            {provider ? t("ssoHub.modal.titleEdit") : t("ssoHub.modal.titleNew")}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="p-6 space-y-5">
          {/* Quick presets */}
          {!provider && (
            <div>
              <p className="text-sm font-medium mb-2">{t("ssoHub.modal.startFromTemplate")}</p>
              <div className="flex flex-wrap gap-2">
                {PROVIDER_TYPES.map((type) => (
                  <button
                    key={type}
                    onClick={() => applyPreset(type)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                      form.provider_type === type
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-accent"
                    }`}
                  >
                    {t(`ssoHub.providerTypes.${type}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Field label={t("ssoHub.modal.displayName")} name="name" placeholder={t("ssoHub.modal.displayNamePlaceholder")} />

          {isSaml ? (
            /* ── SAML 2.0 fields ── */
            <>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                  <Shield className="h-4 w-4" /> {t("ssoHub.modal.idpConfigTitle")}
                </p>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Field
                      label={t("ssoHub.modal.idpMetaUrl")}
                      name="idp_metadata_url"
                      placeholder="https://idp.example.com/metadata.xml"
                    />
                  </div>
                  {provider && (
                    <div className="flex items-end">
                      <button
                        onClick={fetchIdpMetadata}
                        disabled={fetchingMeta}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg hover:bg-accent disabled:opacity-50"
                      >
                        <RefreshCw className={`h-4 w-4 ${fetchingMeta ? "animate-spin" : ""}`} />
                        {t("ssoHub.modal.fetch")}
                      </button>
                    </div>
                  )}
                </div>
                <Field label={t("ssoHub.modal.idpEntityId")} name="idp_entity_id" placeholder="https://idp.example.com/saml2" />
                <Field label={t("ssoHub.modal.idpSsoUrl")} name="idp_sso_url" placeholder="https://idp.example.com/saml2/idp/SSO" />
                <Field label={t("ssoHub.modal.idpSloUrl")} name="idp_slo_url" placeholder="https://idp.example.com/saml2/idp/SLO" />
                <TextArea
                  label={t("ssoHub.modal.idpCert")}
                  name="idp_certificate"
                  placeholder="MIICpDCCAYwCCQD..."
                  rows={5}
                />
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                  <KeyRound className="h-4 w-4" /> {t("ssoHub.modal.spConfigTitle")}
                </p>
                <Field label={t("ssoHub.modal.spEntityId")} name="sp_entity_id" placeholder="https://mawered.com/api/v1/sso/saml/[id]/metadata" />
                <Field label={t("ssoHub.modal.spAcsOverride")} name="sp_acs_url_override" placeholder="https://mawered.com/api/v1/sso/saml/[id]/acs" />
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.sign_requests || false}
                      onChange={(e) => setForm({ ...form, sign_requests: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">{t("ssoHub.modal.signAuthn")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.want_assertions_signed !== false}
                      onChange={(e) => setForm({ ...form, want_assertions_signed: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">{t("ssoHub.modal.requireSignedAssertions")}</span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            /* ── OIDC / OAuth2 fields ── */
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("ssoHub.modal.tenantId")} name="tenant_id" placeholder={t("ssoHub.modal.tenantIdPlaceholder")} />
                <Field label={t("ssoHub.modal.clientId")} name="client_id" />
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">{t("ssoHub.modal.clientSecret")}</label>
                  <div className="relative">
                    <input
                      type={showSecret ? "text" : "password"}
                      value={form.client_secret || ""}
                      onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm bg-background pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-2 top-2 text-muted-foreground"
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">{t("ssoHub.modal.oidcUrls")}</p>
                <Field label={t("ssoHub.modal.discoveryUrl")} name="discovery_url" />
                <Field label={t("ssoHub.modal.authUrl")} name="authorization_url" />
                <Field label={t("ssoHub.modal.tokenUrl")} name="token_url" />
                <Field label={t("ssoHub.modal.userinfoUrl")} name="userinfo_url" />
              </div>
            </>
          )}

          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">{t("ssoHub.modal.attrMapping")}</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t("ssoHub.modal.emailAttr")} name="attr_email" placeholder="email" />
              <Field label={t("ssoHub.modal.firstNameAttr")} name="attr_first_name" placeholder="given_name" />
              <Field label={t("ssoHub.modal.lastNameAttr")} name="attr_last_name" placeholder="family_name" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_create_users}
                onChange={(e) => setForm({ ...form, auto_create_users: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">{t("ssoHub.modal.autoCreate")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">{t("ssoHub.modal.providerActive")}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t("ssoHub.modal.defaultRole")}</label>
            <select
              value={form.default_role}
              onChange={(e) => setForm({ ...form, default_role: e.target.value })}
              className="border rounded-lg px-3 py-2 text-sm bg-background w-full"
            >
              {["EMPLOYE", "SUPERVISOR", "RH", "ADMIN"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="sticky bottom-0 bg-background border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-accent">
            {t("common.cancel")}
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {provider ? t("common.save") : t("common.create")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sessions log ───────────────────────────────────────────────────────────── */
function SessionsTab() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    apiClient.get("v1/sso/sessions?limit=100").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-card border rounded-xl overflow-hidden">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">{t("ssoHub.sessions.title")}</h3>
        <span className="text-sm text-muted-foreground">{t("ssoHub.sessions.entries", { count: sessions.length })}</span>
      </div>
      {sessions.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm">
          {t("ssoHub.sessions.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                {[t("ssoHub.sessions.colId"), t("ssoHub.sessions.colProvider"), t("ssoHub.sessions.colUser"), t("ssoHub.sessions.colStatus"), t("ssoHub.sessions.colIp"), t("ssoHub.sessions.colDate")].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="border-b hover:bg-accent/30">
                  <td className="px-4 py-3 text-muted-foreground">#{s.id}</td>
                  <td className="px-4 py-3">{s.provider_id}</td>
                  <td className="px-4 py-3">{s.user_id ? `#${s.user_id}` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || "bg-gray-100 text-gray-600"}`}>
                      {s.status === "completed" && <CheckCircle className="inline h-3 w-3 mr-1" />}
                      {s.status === "failed" && <XCircle className="inline h-3 w-3 mr-1" />}
                      {s.status === "pending" && <Clock className="inline h-3 w-3 mr-1" />}
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.ip_address || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function LoadingState() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
      {t("ssoHub.loading")}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="text-center py-16 border rounded-xl bg-card">
      <Icon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
